/**
 * Public-website affiliate-link cap helper (task 6.3, public-website spec).
 *
 * Pure function projecting an arbitrary affiliate-link array down to
 * the per-content / per-game maximum of 10 links per **R12.3** / parent
 * `dvgup-platform` design §13 **Property 17**. The helper is the
 * single canonical place the SPA enforces the cap; every consumer
 * (Game-page selector in task 6.6's `selectGameDetailWithAffiliates`,
 * Daily-page `<ContentCard>` affiliate hydration, the future
 * affiliate thunk in task 6.5) pipes its raw link list through this
 * function before handing it to a renderer.
 *
 * The presentational `<AffiliateLinks>` component (task 5.4) is
 * intentionally NOT cap-aware — it renders whatever it is given —
 * so that the property-based co-test for the cap can exercise this
 * helper directly without DOM scaffolding (see task 12.1's
 * `tst/lib/helpers/capped-affiliate-links.test.ts` fast-check
 * **Property 17** co-test, separate from the deterministic boundary
 * tests landed alongside this file in task 6.11).
 *
 * The function is **generic** so it composes against any
 * affiliate-link-shaped record. The two concrete affiliate-link
 * shapes in the SPA today are:
 *
 * - `AffiliateLink` from
 *   {@link ../api/affiliate-signed-url-client.ts} (the signed-URL
 *   transport's hop-2 payload — `{ vendor, url }`)
 * - `GameAffiliateLink` from {@link ../api/games-api-client.ts} (the
 *   `GameDetail.affiliateLinks` shape — `{ vendor, url }`)
 *
 * Both pass through the helper unchanged. Future affiliate-link
 * shapes (e.g., a `ContentAffiliateLink` for the Daily page) will
 * compose the same way without changes here.
 *
 * Cap-handling decisions:
 *
 * - **Default cap.** Omitting `cap` resolves to `10` per R12.3 /
 *   Property 17 — the parent meta-spec's per-page cap.
 * - **`cap === 0`.** Returns an empty array. (`Array.prototype.slice
 *   (0, 0)` is the exact behavior we want.)
 * - **Negative or non-finite cap.** Treated as `0` and returns an
 *   empty array. This guards against caller bugs (e.g., a
 *   misconfigured `BrandingConfig.affiliateCap` resolving to `-1`
 *   or `NaN`) flooding the page rather than failing closed.
 * - **`cap >= links.length`.** Returns a copy of the full array.
 *
 * Return-shape decision: this helper **always** returns a freshly
 * allocated array (via `Array.prototype.slice`). It does NOT
 * preserve referential equality with the input even when no cap is
 * applied. Callers that depend on referential equality (e.g.,
 * `reselect` memoization) should compose this helper inside the
 * selector's projection function so the new-array allocation is
 * itself memoized; see task 6.6's `selectGameDetailWithAffiliates`.
 *
 * Pure: no I/O, no global state, no input mutation. Called once per
 * Game-page mount and once per Daily-page mount in the current SPA.
 *
 * _Requirements: R12.3, Property 17, design §6.5._
 */

/** Default per-content / per-game affiliate-link cap per R12.3 / Property 17. */
export const DEFAULT_AFFILIATE_LINK_CAP = 10;

/**
 * Project an affiliate-link array down to at most `cap` entries,
 * preserving order, returning a fresh array.
 *
 * @typeParam T - The link shape; generic so the same helper applies
 *   to every `{ vendor, url }`-style affiliate-link record across
 *   the SPA (see file header for the concrete shapes).
 * @param links - The raw affiliate links from the API response.
 * @param cap - The maximum number of links to return; defaults to
 *   {@link DEFAULT_AFFILIATE_LINK_CAP} (10 per R12.3 / Property 17).
 *   Negative or non-finite values are treated as `0`.
 * @returns A new array of at most `cap` entries from the front of
 *   `links`, in input order. The input is not mutated.
 *
 * @example
 * ```ts
 * cappedAffiliateLinks(detail.affiliateLinks); // default cap = 10
 * cappedAffiliateLinks(detail.affiliateLinks, 5); // explicit cap
 * ```
 */
export function cappedAffiliateLinks<T>(
  links: ReadonlyArray<T>,
  cap: number = DEFAULT_AFFILIATE_LINK_CAP,
): ReadonlyArray<T> {
  const safeCap = Number.isFinite(cap) && cap > 0 ? Math.floor(cap) : 0;
  return links.slice(0, safeCap);
}
